'use client'

import { useEffect, useRef } from 'react'

import type {
  ChannelName,
  RealtimeMessageHandlerFunction,
} from '@mntn-dev/realtime-updates-types'

import { realtimeClientService } from './realtime-client-service.ts'
import { realtimeUpdateLogger } from './realtime-update-logger.ts'
import type { RealtimeSchema } from './schemas.ts'

const useHandleAllMessages = (
  channelNames: ChannelName[],
  messageHandler: RealtimeMessageHandlerFunction<RealtimeSchema>
) => {
  const channels = useRef<
    Map<
      ChannelName,
      NonNullable<ReturnType<typeof realtimeClientService.getChannel>>
    >
  >(new Map())

  useEffect(() => {
    const messageHandlerWrapper: RealtimeMessageHandlerFunction<
      RealtimeSchema
    > = (message) => {
      realtimeUpdateLogger.debug(
        '---> Handling realtime message (useHandleAllMessages)',
        { message }
      )
      return messageHandler(message)
    }

    // Unsubscribe from removing channels
    for (const [name, channel] of channels.current.entries()) {
      if (!channelNames.includes(name)) {
        channel.unsubscribe()
        channel.detach()
        channels.current.delete(name)
      }
    }

    // Subscribe to adding channels
    for (const name of channelNames) {
      if (!channels.current.has(name)) {
        const channel = realtimeClientService.getChannel(name)
        if (channel) {
          channels.current.set(name, channel)
          realtimeClientService.handleAllMessages(
            channel,
            messageHandlerWrapper
          )
        }
      }
    }

    return () => {
      for (const channel of channels.current.values()) {
        channel.unsubscribe()
        channel.detach()
      }
      channels.current.clear()
    }
  }, [channelNames, messageHandler])

  return channels.current
}

export { useHandleAllMessages }
